import { useDispatch, useSelector } from 'react-redux';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { change_Current_Location, changeHeaderTitle } from '../../store';
import axios from 'axios';
import { MessageModal } from '../component/modal/modal';
import { LoadingModal } from '../component/modal/modal';
import TreeList from '../component/list/treeList';
import List from '../component/list/list';
import Layout from './components/Layout';
import dayjs from 'dayjs';
import { changeNum } from '../common/function';
import bank from '../bankList';
import { useAccountHolderVerify, verifyBtnStyle, verifiedBadgeStyle } from '../hook/useAccountHolderVerify';
import './styles/v2.css';

function IntegrateAgentV2() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const MyData = useSelector((state) => state.MyData);
    const brand = useSelector((state) => state.brand);

    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState("");

    // 트리 전체 (지사/총판/대리점)
    const [listData, setListData] = useState([]);
    // 검색/토글/접기/펴기로 현재 화면에 표시 중인 트리
    const [filterTreeData, setFilterTreeData] = useState([]);
    // 테이블에 표시할 flatten(1차원) 배열
    const [combineData, setCombineData] = useState([]);
    // 총 에이전트 수
    const [agentLength, setAgentLength] = useState(0);

    // 매출 100% 기준 (1단계 중 최댓값)
    const [maxRootSales, setMaxRootSales] = useState(0);

    // 모달
    const [createModal, setCreateModal] = useState(false);
    const [updateModal, setUpdataeModal] = useState(false);
    const [clickData, setClickData] = useState(null);

    // 검색
    const [searchKeyword, setSearchKeyword] = useState("");
    const [levelFilter, setLevelFilter] = useState("all");
    const [minSales, setMinSales] = useState("");
    const [maxSales, setMaxSales] = useState("");
    const [searchSuggestions, setSearchSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    // 인라인 편집
    const [editingNodeId, setEditingNodeId] = useState(null);
    const [editingField, setEditingField] = useState(null);
    const [editingValue, setEditingValue] = useState("");

    // 리렌더 트리거
    const [reload, setReload] = useState(false);
    const [listState, setListState] = useState(false);

    const [sortAmt, setSortAmt] = useState(0);

    useEffect(() => {
        dispatch(change_Current_Location("통합에이전트"));
        dispatch(changeHeaderTitle({
            mainTitle: "에이전트관리 (V2)",
            subTitle: "통합에이전트"
        }));

        setTimeout(() => setLoading(false), 500);
    }, [dispatch]);

    // 서버에서 트리 데이터 가져오기
    useEffect(() => {
        fetchAgentData();
    }, [reload]);

    const fetchAgentData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(process.env.REACT_APP_API + "/api/agent", {
                withCredentials: true,
            });

            if (res.data?.code < 2000) {
                const data = res.data?.data?.newAgent ?? [];
                // 1) 트리 기본 구조 초기화
                const init = initTreeData(data, 1, null);

                // 2) 1단계 노드 중 최댓값 찾기
                const maxRoot = getMaxRootSales(init);
                setMaxRootSales(maxRoot);

                // 3) 스테이트 반영
                setListData(init);
                setAgentLength(res.data?.data?.count ?? 0);
            } else if (res.data?.code === 9999) {
                navigate("/");
            }
        } catch (err) {
            console.error(err);
            navigate("/");
        } finally {
            setLoading(false);
        }
    };

    // (A) parent 참조 + 레벨정보 세팅
    function initTreeData(arr, level, parent) {
        return arr.map((item) => {
            const newItem = {
                ...item,
                _expanded: false,
                _level: level,
                parent: parent || null,
            };
            if (item.children && item.children.length > 0) {
                newItem.children = initTreeData(item.children, level + 1, newItem);
            } else {
                newItem.children = [];
            }
            return newItem;
        });
    }

    // (D) 1단계 노드 중 가장 큰 매출 찾기
    function getMaxRootSales(nodes) {
        let maxSales = 0;
        for (const node of nodes) {
            if (node._level === 1) {
                if ((node.trx_amt || 0) > maxSales) {
                    maxSales = node.trx_amt;
                }
            }
            if (node.children && node.children.length > 0) {
                const childMax = getMaxRootSales(node.children);
                if (childMax > maxSales) {
                    maxSales = childMax;
                }
            }
        }
        return maxSales;
    }

    // 통합 필터링 로직 (검색 + 계층 + 매출 범위)
    useEffect(() => {
        let filtered = listData;

        // 1) 계층 필터
        if (levelFilter !== "all") {
            filtered = filterByLevel(listData, Number(levelFilter));
        }

        // 2) 검색 필터
        const keyword = searchKeyword.trim();
        if (keyword) {
            const allNodes = [];
            flattenTreeAll(filtered, allNodes);
            const matchedSet = new Set();
            for (const node of allNodes) {
                const nmMatch =
                    node.nm && node.nm.toLowerCase().includes(keyword.toLowerCase());
                const idMatch =
                    node.agent_id &&
                    node.agent_id.toLowerCase().includes(keyword.toLowerCase());
                if (nmMatch || idMatch) {
                    addAncestors(node, matchedSet);
                    addDescendants(node, matchedSet);
                }
            }
            filtered = keepNodeAndAllChildren(filtered, matchedSet);
            const openedAll = openAllExpanded(filtered);
            filtered = openedAll;
        }

        // 3) 매출 범위 필터
        if (minSales || maxSales) {
            filtered = filterBySalesRange(filtered, minSales, maxSales);
        }

        // 검색어가 없으면 모두 접기
        if (!keyword) {
            const closedAll = closeAllExpanded(filtered);
            setFilterTreeData(closedAll);
        } else {
            setFilterTreeData(filtered);
        }
    }, [listData, searchKeyword, levelFilter, minSales, maxSales]);

    function filterByLevel(nodes, targetLevel) {
        return nodes.filter(node => {
            if (node._level === targetLevel) {
                return true;
            }
            if (node.children && node.children.length > 0) {
                const filteredChildren = filterByLevel(node.children, targetLevel);
                if (filteredChildren.length > 0) {
                    return true;
                }
            }
            return false;
        }).map(node => ({
            ...node,
            children: node.children ? filterByLevel(node.children, targetLevel) : []
        }));
    }

    function filterBySalesRange(nodes, min, max) {
        return nodes.filter(node => {
            const sales = node.trx_amt || 0;
            const meetsMin = !min || sales >= Number(min);
            const meetsMax = !max || sales <= Number(max);
            return meetsMin && meetsMax;
        }).map(node => ({
            ...node,
            children: node.children ? filterBySalesRange(node.children, min, max) : []
        }));
    }

    function flattenTreeAll(nodes, output) {
        for (const node of nodes) {
            output.push(node);
            if (node.children && node.children.length) {
                flattenTreeAll(node.children, output);
            }
        }
    }
    function addAncestors(node, matchedSet) {
        let cur = node;
        while (cur) {
            matchedSet.add(cur);
            cur = cur.parent;
        }
    }
    function addDescendants(node, matchedSet) {
        matchedSet.add(node);
        if (!node.children || node.children.length === 0) return;
        for (const c of node.children) {
            addDescendants(c, matchedSet);
        }
    }
    function keepNodeAndAllChildren(originalNodes, matchedSet) {
        const result = [];
        for (const node of originalNodes) {
            if (matchedSet.has(node)) {
                const newNode = { ...node };
                newNode.children = keepNodeAndAllChildren(node.children, matchedSet);
                result.push(newNode);
            } else {
                const filteredChildren = keepNodeAndAllChildren(node.children, matchedSet);
                if (filteredChildren.length > 0) {
                    const newNode = { ...node };
                    newNode.children = filteredChildren;
                    result.push(newNode);
                }
            }
        }
        return result;
    }

    // filterTreeData가 바뀔 때마다 flatten → combineData
    useEffect(() => {
        if (!filterTreeData || filterTreeData.length === 0) {
            setCombineData([]);
            return;
        }

        // 1) 트리 정렬
        function sortTreeByAmt(nodes) {
            if (!Array.isArray(nodes)) return [];
            const arr = [...nodes];
            if (sortAmt === 1) {
                arr.sort((a, b) => (b.trx_amt || 0) - (a.trx_amt || 0));
            } else if (sortAmt === 2) {
                arr.sort((a, b) => (a.trx_amt || 0) - (b.trx_amt || 0));
            }
            return arr.map(node => ({
                ...node,
                children: sortTreeByAmt(node.children)
            }));
        }

        const treeToFlatten =
            (sortAmt === 1 || sortAmt === 2)
                ? sortTreeByAmt(filterTreeData)
                : filterTreeData;

        // 2) 플랫하게 펼치기
        const flat = [];
        flattenTree(treeToFlatten, flat);

        // 3) 테이블 빌드
        setCombineData(buildTableRowArray(flat));
    }, [filterTreeData, sortAmt]);

    // 펼쳐진 노드만 flatten
    function flattenTree(nodes, output, ancestors = []) {
        nodes.forEach((node, idx) => {
            const isLastChild = idx === nodes.length - 1;
            node._ancestorLastChild = ancestors;
            node._isLastChild = isLastChild;

            output.push(node);

            if (node._expanded && node.children && node.children.length) {
                flattenTree(node.children, output, [...ancestors, isLastChild]);
            }
        });
    }

    // 아이콘 클릭 → 하위 펼침/접기
    function handleIconClick(e, node) {
        e.stopPropagation();
        setFilterTreeData((prev) => toggleExpandedInTree(prev, node.id));
    }

    // 하위 매출 합계 계산 함수
    function calculateChildSales(node) {
        if (!node.children || node.children.length === 0) {
            return 0;
        }
        return node.children.reduce((sum, child) => {
            return sum + (child.trx_amt || 0) + calculateChildSales(child);
        }, 0);
    }

    // 테이블 바디(각 행) 구성
    function buildTableRowArray(flatData) {
        let tableBodyData = [];

        for (let i = 0; i < flatData.length; i++) {
            const node = flatData[i];

            // 1) 단순화된 트리 아이콘 (인덴트 + 토글 버튼)
            const indentLevel = node._ancestorLastChild?.length || 0;
            const indentElements = [];
            for (let j = 0; j < indentLevel; j++) {
                const isLast = node._ancestorLastChild[j];
                indentElements.push(
                    <div
                        key={j}
                        style={{
                            width: "20px",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative"
                        }}
                    >
                        {!isLast && (
                            <div style={{
                                position: "absolute",
                                left: "9px",
                                top: 0,
                                bottom: 0,
                                width: "1px",
                                background: "#d1d5db"
                            }} />
                        )}
                    </div>
                );
            }

            // 토글 버튼
            const toggleButton = node.children && node.children.length > 0 ? (
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleIconClick(e, node);
                    }}
                    style={{
                        width: "18px",
                        height: "18px",
                        border: "1px solid #d1d5db",
                        background: "white",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        padding: 0,
                        marginRight: "4px",
                        lineHeight: "1"
                    }}
                    onMouseEnter={(e) => e.target.style.background = "#f0f0f0"}
                    onMouseLeave={(e) => e.target.style.background = "white"}
                >
                    {node._expanded ? "−" : "+"}
                </button>
            ) : (
                <div style={{ width: "22px", marginRight: "4px" }} />
            );

            // 텔레그램 아이콘 렌더링
            const renderTelegramIcon = () => {
                if (!node.telegram_id) {
                    return null;
                }
                const isActive = node.telegram_noti_yn === 'Y';
                return (
                    <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                            marginLeft: "6px",
                            flexShrink: 0
                        }}
                    >
                        <path
                            d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.13-.31-1.09-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z"
                            fill={isActive ? '#0088cc' : '#ffffff'}
                            stroke={isActive ? '#0088cc' : '#cccccc'}
                            strokeWidth={isActive ? '0' : '1'}
                        />
                    </svg>
                );
            };

            // 인라인 편집 셀
            const agentNameCell = (
                <div
                    style={{
                        position: "absolute",
                        top: "50%",
                        transform: "translateY(-50%)",
                        left: "10px",
                        height: "100%",
                        display: "flex",
                        alignItems: "center"
                    }}
                >
                    {indentElements}
                    {toggleButton}
                    {editingNodeId === node.id && editingField === 'nm' ? (
                        <input
                            type="text"
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={() => {
                                if (editingValue !== node.nm) {
                                    handleSaveEdit(node.id, 'nm', editingValue);
                                }
                                setEditingNodeId(null);
                                setEditingField(null);
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    if (editingValue !== node.nm) {
                                        handleSaveEdit(node.id, 'nm', editingValue);
                                    }
                                    setEditingNodeId(null);
                                    setEditingField(null);
                                } else if (e.key === 'Escape') {
                                    setEditingNodeId(null);
                                    setEditingField(null);
                                }
                            }}
                            autoFocus
                            style={{
                                padding: "2px 4px",
                                border: "1px solid #3b82f6",
                                fontSize: "12px",
                                width: "150px"
                            }}
                        />
                    ) : (
                        <div
                            onDoubleClick={() => {
                                if (MyData?.role === "SUPER") {
                                    setEditingNodeId(node.id);
                                    setEditingField('nm');
                                    setEditingValue(node.nm);
                                }
                            }}
                            style={{
                                display: "flex",
                                alignItems: "center",
                                paddingLeft: "3px",
                                cursor: MyData?.role === "SUPER" ? "pointer" : "default"
                            }}
                        >
                            <span>{node.nm}</span>
                            {node?.children?.length > 0 ? <span style={{ marginLeft: "4px", color: "#666", fontSize: "11px" }}>({node?.children?.length})</span> : ""}
                            {renderTelegramIcon()}
                        </div>
                    )}
                </div>
            );

            // 매출 셀 (진행바 + 하위 합계)
            const salesValue = node.trx_amt || 0;
            const childSales = calculateChildSales(node);
            const totalSales = salesValue + childSales;
            const ratio = maxRootSales > 0 ? (salesValue / maxRootSales) : 0;
            const barColor = ratio > 0.7 ? "#dc2626" : ratio > 0.4 ? "#f59e0b" : "#10b981";

            const salesCell = (
                <div style={{ position: "relative", width: "100%", height: "100%", paddingRight: "6px" }}>
                    {/* 진행바 배경 */}
                    <div style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "#f0f0f0"
                    }}>
                        <div style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            bottom: 0,
                            width: `${ratio * 100}%`,
                            background: barColor,
                            opacity: 0.3
                        }} />
                    </div>
                    
                    {/* 텍스트 정보 */}
                    <div style={{
                        position: "relative",
                        zIndex: 1,
                        textAlign: "right",
                        height: "100%",
                        lineHeight: "14px",
                        paddingTop: "2px",
                        fontSize: "11px"
                    }}>
                        <div style={{ fontWeight: "bold" }}>
                            {changeNum(Number(salesValue))}
                        </div>
                        {childSales > 0 && (
                            <div style={{ 
                                fontSize: "10px", 
                                color: "#666",
                                lineHeight: "12px"
                            }}>
                                (하위: {changeNum(Number(childSales))})
                            </div>
                        )}
                    </div>
                </div>
            );

            const salesMod = (
                <div style={{ position: "absolute", width: "100%", height: "100%", right: "0px", top: "0px" }}>
                    <div
                        style={{
                            position: "relative",
                            zIndex: 1,
                            textAlign: "center",
                            height: "100%",
                            lineHeight: "28px",
                            userSelect: "none",
                        }}
                    >
                        {node.mod_dt?.slice(2, 16)}
                    </div>
                </div>
            );

            // 에이전트ID 셀
            const agentIdCell = (
                <div style={{ padding: "0 6px", fontSize: "11px", color: "#666" }}>
                    {node.agent_id || "-"}
                </div>
            );

            // 수수료 셀
            const feeCell = (
                <div style={{ padding: "0 6px", fontSize: "11px", textAlign: "right" }}>
                    {node.agent_fee ? `${Number(node.agent_fee)}%` : "-"}
                </div>
            );

            // 정산은행 셀
            const bankCell = (
                <div style={{ padding: "0 6px", fontSize: "11px" }}>
                    {node.bank_nm || "-"}
                </div>
            );

            // 계좌번호 셀
            const accountCell = (
                <div style={{ padding: "0 6px", fontSize: "11px", color: "#666" }}>
                    {node.accnt_no || "-"}
                </div>
            );

            // 예금주 셀
            const accountNameCell = (
                <div style={{ padding: "0 6px", fontSize: "11px" }}>
                    {node.accnt_nm || "-"}
                </div>
            );

            // 텔레그램 알림 셀
            const telegramCell = (
                <div style={{ padding: "0 6px", fontSize: "11px", textAlign: "center" }}>
                    {node.telegram_noti_yn === 'Y' ? (
                        <span style={{ color: "#0088cc" }}>사용</span>
                    ) : node.telegram_id ? (
                        <span style={{ color: "#999" }}>미사용</span>
                    ) : (
                        <span style={{ color: "#ccc" }}>-</span>
                    )}
                </div>
            );

            const rowData = [
                {
                    no: 1,
                    text: agentNameCell,
                    width: "200px",
                    textAlign: "left",
                },
                {
                    no: 2,
                    text: agentIdCell,
                    width: "120px",
                    textAlign: "left",
                },
                {
                    no: 3,
                    text: salesCell,
                    width: "120px",
                    textAlign: "right",
                },
                {
                    no: 5,
                    text: feeCell,
                    width: "70px",
                    textAlign: "right",
                },
                {
                    no: 6,
                    text: bankCell,
                    width: "100px",
                    textAlign: "left",
                },
                {
                    no: 7,
                    text: accountCell,
                    width: "120px",
                    textAlign: "left",
                },
                {
                    no: 8,
                    text: accountNameCell,
                    width: "100px",
                    textAlign: "left",
                },
                {
                    no: 9,
                    text: telegramCell,
                    width: "70px",
                    textAlign: "center",
                },
                {
                    no: 4,
                    text: salesMod,
                    width: "100px",
                    textAlign: "center",
                },
                {
                    no: 6,
                    text: "",
                    width: "auto",
                    textAlign: "left",
                    skeleton: true
                },
            ];

            rowData.id = node.id;
            tableBodyData.push(rowData);
        }

        return tableBodyData;
    }

    // 인라인 편집 저장
    const handleSaveEdit = async (nodeId, field, value) => {
        const foundNode = findNodeById(filterTreeData, nodeId);
        if (!foundNode || MyData?.role !== "SUPER") return;

        try {
            await axios.post(process.env.REACT_APP_API + "/api/agent/update", {
                id: foundNode.id,
                [field === 'nm' ? 'nm' : field]: value
            }, {
                withCredentials: true,
            }).then((response) => {
                let tmp = response?.data;
                if (tmp?.code < 2000) {
                    setMessage({ msg: "수정되었습니다." });
                    setReload(e => !e);
                } else {
                    setMessage({ msg: "수정에 실패했습니다." });
                }
            }).catch(error => {
                setMessage({ msg: "수정에 실패했습니다." });
                console.error(error);
            });
        } catch (error) {
            console.error(error);
        }
    };

    // 행 단일 클릭
    function handleRowClick(row) {
        // 인라인 편집 중이면 클릭 무시
        if (editingNodeId) return;

        const foundNode = findNodeById(filterTreeData, row.id);
        if (foundNode) {
            setClickData(foundNode);
            setCreateModal(false);
            setUpdataeModal(true);
        }
        setListState((e) => !e);
    }

    // 행 더블 클릭 → 하위 펼침/접기
    const handleRowDoubleClick = (row) => {
        const foundNode = findNodeById(filterTreeData, row.id);
        if (foundNode && foundNode.children && foundNode.children.length > 0) {
            setFilterTreeData((prev) => toggleExpandedInTree(prev, foundNode.id));
        }
    };

    // 펼침/접기 토글
    function toggleExpandedInTree(dataArr, targetId) {
        return dataArr.map((item) => {
            if (item.id === targetId) {
                return { ...item, _expanded: !item._expanded };
            }
            if (item.children && item.children.length > 0) {
                return {
                    ...item,
                    children: toggleExpandedInTree(item.children, targetId),
                };
            }
            return { ...item };
        });
    }

    function findNodeById(dataArr, targetId) {
        for (let item of dataArr) {
            if (item.id === targetId) return item;
            if (item.children && item.children.length > 0) {
                const result = findNodeById(item.children, targetId);
                if (result) return result;
            }
        }
        return null;
    }

    // 전체 펴기 / 접기
    function openAllExpanded(arr) {
        return arr.map((item) => ({
            ...item,
            _expanded: true,
            children: item.children ? openAllExpanded(item.children) : [],
        }));
    }

    function closeAllExpanded(arr) {
        return arr.map((item) => ({
            ...item,
            _expanded: false,
            children: item.children ? closeAllExpanded(item.children) : [],
        }));
    }

    const handleOpenAll = () => {
        setFilterTreeData((prev) => openAllExpanded(prev));
        setListState((e) => !e);
    };

    const handleCloseAll = () => {
        setFilterTreeData((prev) => closeAllExpanded(prev));
        setListState((e) => !e);
    };

    // 테이블 헤더
    const tableHeaderData = [
        {
            no: 1,
            text: "에이전트명",
            width: "200px",
            textAlign: "center",
        },
        {
            no: 2,
            text: "에이전트ID",
            width: "120px",
            textAlign: "center",
        },
        {
            no: 3,
            text: (
                <div
                    className={sortAmt === 0 ? 'table-sort' : 'table-sort sort-color'}
                    onClick={() => {
                        setSortAmt(e => (e + 1) % 3);
                    }}
                    style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", cursor: "pointer" }}
                >
                    <span>매출</span>
                    <span>
                        <span className="index_sort-btn__eWsx8">
                            <svg width="9" height="6" viewBox="0 0 5 3" fill="none">
                                <path
                                    className={`${sortAmt === 2 ? "fill-color" : ""}`}
                                    d="M0.342163 3L3.77567 3C3.96395 3 4.11658 2.85941 4.11658 2.68599C4.11658 2.62399 4.09665 2.56339 4.05932 2.5118L2.34257 0.139848C2.23813 -0.00444984 2.02647 -0.0434422 1.86981 0.0527565C1.83236 0.0757532 1.80023 0.105353 1.77526 0.139848L0.0585074 2.5118C-0.0459313 2.6561 -0.00359917 2.85106 0.153059 2.94726C0.20906 2.98165 0.274858 3 0.342163 3Z"
                                    fill={"#999"}
                                />
                            </svg>
                            <svg width="9" height="6" viewBox="0 0 5 3" fill="none">
                                <path
                                    className={`${sortAmt === 1 ? "fill-color" : ""}`}
                                    d="M3.77442 0H0.340911C0.152631 0 0 0.140589 0 0.314014C0 0.376008 0.0199222 0.436615 0.0572559 0.488197L1.77401 2.86015C1.87845 3.00445 2.09011 3.04344 2.24677 2.94724C2.28422 2.92425 2.31635 2.89465 2.34132 2.86015L4.05807 0.488197C4.16251 0.343899 4.12018 0.148937 3.96352 0.0527385C3.90752 0.0183503 3.84172 0 3.77442 0Z"
                                    fill={"#999"}
                                />
                            </svg>
                        </span>
                    </span>
                </div>
            ),
            width: "120px",
            textAlign: "center",
        },
        {
            no: 5,
            text: "수수료",
            width: "70px",
            textAlign: "center",
        },
        {
            no: 6,
            text: "정산은행",
            width: "100px",
            textAlign: "center",
        },
        {
            no: 7,
            text: "계좌번호",
            width: "120px",
            textAlign: "center",
        },
        {
            no: 8,
            text: "예금주",
            width: "100px",
            textAlign: "center",
        },
        {
            no: 9,
            text: "텔레그램",
            width: "70px",
            textAlign: "center",
        },
        {
            no: 4,
            text: "수정일",
            width: "100px",
            textAlign: "center",
        },
        {
            no: 6,
            text: "",
            width: "auto",
            textAlign: "left",
            skeleton: true
        },
    ];

    let tabList = MyData?.role == "SUPER" ? ["에이전트 수정", "가맹점 목록"] : ["에이전트 상세", "가맹점 목록"]
    const [activeTab, setActiveTab] = useState(MyData?.role == "SUPER" ? "에이전트 수정" : "에이전트 상세")

    const handleClickTab = (tabName) => {
        setActiveTab(tabName);
    };

    return (
        <Layout>
            {loading && <LoadingModal />}
            <MessageModal message={message} />

            <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                height: '100%', 
                overflow: 'hidden',
                padding: '16px',
                gap: '12px'
            }}>
                {/* 검색 영역 */}
                <div style={{ 
                    background: "#f9fafb", 
                    border: "1px solid #d1d5db", 
                    padding: "12px",
                    flexShrink: 0
                }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                        <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>검색:</span>
                        <div style={{ position: "relative" }}>
                            <input
                                type="text"
                                placeholder="에이전트명 또는 에이전트ID"
                                value={searchKeyword}
                                onChange={(e) => {
                                    setSearchKeyword(e.target.value);
                                    // 자동완성 로직
                                    const allNodes = [];
                                    flattenTreeAll(listData, allNodes);
                                    const suggestions = allNodes
                                        .filter(n => 
                                            (n.nm && n.nm.toLowerCase().includes(e.target.value.toLowerCase())) ||
                                            (n.agent_id && n.agent_id.toLowerCase().includes(e.target.value.toLowerCase()))
                                        )
                                        .slice(0, 5)
                                        .map(n => n.nm || n.agent_id);
                                    setSearchSuggestions(suggestions);
                                    setShowSuggestions(e.target.value.length > 0);
                                }}
                                onFocus={() => setShowSuggestions(searchKeyword.length > 0)}
                                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                style={{
                                    padding: "6px 12px",
                                    fontSize: "12px",
                                    border: "1px solid #d1d5db",
                                    background: "white",
                                    width: "300px"
                                }}
                            />
                            {showSuggestions && searchSuggestions.length > 0 && (
                                <div style={{
                                    position: "absolute",
                                    top: "100%",
                                    left: 0,
                                    right: 0,
                                    background: "white",
                                    border: "1px solid #d1d5db",
                                    borderTop: "none",
                                    zIndex: 1000,
                                    maxHeight: "200px",
                                    overflowY: "auto"
                                }}>
                                    {searchSuggestions.map((suggestion, idx) => (
                                        <div
                                            key={idx}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                                setSearchKeyword(suggestion);
                                                setShowSuggestions(false);
                                            }}
                                            style={{
                                                padding: "6px 12px",
                                                cursor: "pointer",
                                                fontSize: "12px"
                                            }}
                                            onMouseEnter={(e) => e.target.style.background = "#f0f0f0"}
                                            onMouseLeave={(e) => e.target.style.background = "white"}
                                        >
                                            {suggestion}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>계층:</span>
                        <select
                            value={levelFilter}
                            onChange={(e) => setLevelFilter(e.target.value)}
                            style={{
                                padding: "6px 12px",
                                fontSize: "12px",
                                border: "1px solid #d1d5db",
                                background: "white"
                            }}
                        >
                            <option value="all">전체</option>
                            <option value="1">지사</option>
                            <option value="2">총판</option>
                            <option value="3">대리점</option>
                            <option value="4">영업자1</option>
                            <option value="5">영업자2</option>
                            <option value="6">영업자3</option>
                        </select>
                        
                        <span style={{ fontSize: "12px", color: "#666", fontWeight: "500" }}>매출:</span>
                        <input
                            type="number"
                            placeholder="최소"
                            value={minSales}
                            onChange={(e) => setMinSales(e.target.value)}
                            style={{
                                padding: "6px 12px",
                                fontSize: "12px",
                                border: "1px solid #d1d5db",
                                background: "white",
                                width: "100px"
                            }}
                        />
                        <span>~</span>
                        <input
                            type="number"
                            placeholder="최대"
                            value={maxSales}
                            onChange={(e) => setMaxSales(e.target.value)}
                            style={{
                                padding: "6px 12px",
                                fontSize: "12px",
                                border: "1px solid #d1d5db",
                                background: "white",
                                width: "100px"
                            }}
                        />
                        
                        <button
                            onClick={() => {
                                setSearchKeyword("");
                                setLevelFilter("all");
                                setMinSales("");
                                setMaxSales("");
                                setClickData(null);
                            }}
                            style={{
                                padding: "6px 12px",
                                fontSize: "12px",
                                background: "white",
                                border: "1px solid #d1d5db",
                                cursor: "pointer"
                            }}
                        >
                            초기화
                        </button>
                    </div>
                </div>

                {/* 메인 컨텐츠 영역 */}
                <div style={{ 
                    display: "flex", 
                    gap: "16px", 
                    flex: 1, 
                    minHeight: 0, 
                    overflow: "hidden" 
                }}>
                    {/* 좌측: 트리 테이블 */}
                    <div style={{ 
                        flex: "1 1 70%", 
                        minWidth: "800px",
                        display: "flex", 
                        flexDirection: "column",
                        background: "white",
                        border: "1px solid #d1d5db",
                        overflow: "hidden"
                    }}>
                        <div style={{ 
                            display: "flex", 
                            alignItems: "center", 
                            justifyContent: "space-between",
                            padding: "8px 12px",
                            background: "#f5f5f5",
                            borderBottom: "1px solid #d1d5db",
                            fontSize: "12px",
                            flexShrink: 0
                        }}>
                            <span style={{ color: "#666" }}>총 {agentLength}건</span>
                            <div style={{ display: "flex", gap: "8px" }}>
                                <button
                                    type="button"
                                    onClick={handleOpenAll}
                                    style={{ 
                                        padding: "4px 8px",
                                        fontSize: "11px",
                                        background: "white",
                                        border: "1px solid #d1d5db",
                                        cursor: "pointer"
                                    }}
                                >
                                    펴기
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCloseAll}
                                    style={{ 
                                        padding: "4px 8px",
                                        fontSize: "11px",
                                        background: "white",
                                        border: "1px solid #d1d5db",
                                        cursor: "pointer"
                                    }}
                                >
                                    접기
                                </button>
                            </div>
                        </div>

                        <div style={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
                            <TreeList
                                tableHeaderData={tableHeaderData}
                                tableBodyData={filterTreeData ?? []}
                                filter_tableBodyData={combineData}
                                clickData={clickData}
                                onRowClick={handleRowClick}
                                onRowDoubleClick={handleRowDoubleClick}
                                listState={listState}
                                nonePage={false}
                                height={"100%"}
                                scrollState={true}
                            />
                        </div>
                    </div>

                    {/* 우측: 등록/수정 모달 */}
                    <div style={{ 
                        flex: "0 0 30%",
                        minWidth: "400px",
                        maxWidth: "500px",
                        display: "flex",
                        flexDirection: "column",
                        background: "white",
                        border: "1px solid #d1d5db",
                        overflow: "hidden"
                    }}>
                        {!createModal && !updateModal && !clickData && (
                            <div style={{ 
                                width: "100%", 
                                height: "100%", 
                                display: "flex", 
                                alignItems: "center", 
                                justifyContent: "center", 
                                fontSize: "16px", 
                                fontWeight: "bold", 
                                color: "gray" 
                            }}>
                                에이전트를 선택해주세요
                            </div>
                        )}
                        {createModal && MyData?.role === "SUPER" && (
                            <div style={{ padding: "16px", height: "100%", overflow: "auto" }}>
                                <div style={{ 
                                    display: "flex",
                                    gap: "10px",
                                    alignItems: "center",
                                    padding: "0 10px",
                                    borderBottom: "1px solid #E5E5E5",
                                    backgroundColor: "#fff",
                                    marginBottom: "16px"
                                }}>
                                    <button
                                        className="tab-btn active"
                                        style={{
                                            padding: "12px 10px 8px",
                                            fontSize: "14px",
                                            fontWeight: "600",
                                            color: "#222",
                                            background: "transparent",
                                            border: "none",
                                            borderBottom: "3px solid #3182F6",
                                            cursor: "pointer",
                                        }}
                                    >
                                        에이전트 등록
                                    </button>
                                </div>
                                <CreateInfo setMessage={setMessage} setReload={setReload} />
                            </div>
                        )}
                        {updateModal && clickData && (
                            <div style={{ padding: "16px", height: "100%", overflow: "auto" }}>
                                <div style={{ 
                                    display: "flex",
                                    gap: "10px",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    padding: "0 10px",
                                    borderBottom: "1px solid #E5E5E5",
                                    backgroundColor: "#fff",
                                    marginBottom: "16px"
                                }}>
                                    <div style={{ display: "flex", gap: "10px" }}>
                                        {tabList.map((tab) => (
                                            <button
                                                key={tab}
                                                className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                                                onClick={() => handleClickTab(tab)}
                                                style={{
                                                    padding: "12px 10px 8px",
                                                    fontSize: "14px",
                                                    fontWeight: activeTab === tab ? "600" : "400",
                                                    color: activeTab === tab ? "#222" : "#888",
                                                    background: "transparent",
                                                    border: "none",
                                                    borderBottom: activeTab === tab ? "3px solid #3182F6" : "3px solid transparent",
                                                    cursor: "pointer"
                                                }}
                                            >
                                                {tab}
                                            </button>
                                        ))}
                                    </div>
                                    {MyData?.role === "SUPER" && (
                                        <button
                                            onClick={() => {
                                                setUpdataeModal(false);
                                                setCreateModal(true);
                                                setClickData(null);
                                            }}
                                            style={{
                                                padding: "6px 12px",
                                                fontSize: "12px",
                                                background: "#3b82f6",
                                                color: "white",
                                                border: "none",
                                                cursor: "pointer"
                                            }}
                                        >
                                            신규등록
                                        </button>
                                    )}
                                </div>
                                {(activeTab === "에이전트 수정" || activeTab === "에이전트 상세") && (
                                    <UpdateInfo
                                        setMessage={setMessage}
                                        clickData={clickData}
                                        setReload={setReload}
                                    />
                                )}
                                {(activeTab === "가맹점 목록") && (
                                    <SubmerchantsList
                                        setMessage={setMessage}
                                        clickData={clickData}
                                        setReload={setReload}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </Layout>
    );
}

function CreateInfo({ setMessage, setReload }) {
    const MyData = useSelector((state) => state.MyData);
    const initialFormData = {
        에이전트등급: '',
        상위에이전트: '',
        에이전트ID: '',
        에이전트명: '',
        에이전트수수료: '',
        정산은행: '',
        정산은행코드: '',
        계좌번호: '',
        예금주: '',
        세금계산서Email: '',
        PG코드: '',
        입금적요: '',
        텔레그램아이디: '',
        텔레그램알림사용여부: 'N',
        출금수수료: '',
        지갑사용여부: 'N'
    };

    const [formData, setFormData] = useState(initialFormData);
    const [levelData, setLevelData] = useState([])

    // 예금주 조회
    const { verifying: verifyingHolder, isVerified: holderVerified, verify: verifyHolder } =
        useAccountHolderVerify({ formData, setFormData, setMessage });

    useEffect(() => {
        LevelList()
    }, [formData.에이전트등급])

    const LevelList = async () => {
        await axios.get(process.env.REACT_APP_API + `/api/agent/up-list`, {
            withCredentials: true,
            params: {
                level: Number(formData.에이전트등급)
            }
        }).then((response) => {
            let tmp = response?.data
            if (tmp?.code < 2000) {
                setLevelData(tmp?.data?.agent ?? [])
            }
            else {
                setMessage({ msg: "상위리스트 업로드에 실패했습니다." })
            }
        }).catch(error => {
            console.error(error);
        });
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === '정산은행') {
            const selected = bank.find(b => b.name === value);
            setFormData({
                ...formData,
                정산은행: value,
                정산은행코드: selected?.code || ''
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            await axios.post(process.env.REACT_APP_API + "/api/agent/regist", {
                auth_cd: formData.에이전트등급,
                agent_id: formData.에이전트ID,
                nm: formData.에이전트명,
                agent_fee: formData.에이전트수수료,
                parent_code: formData.상위에이전트,
                bank_nm: formData.정산은행,
                bank_cd: formData.정산은행코드,
                accnt_no: formData.계좌번호,
                accnt_nm: formData.예금주,
                // tax_email: formData.세금계산서Email,
                worker: 'admin',
                co_id: formData.PG코드,
                payout_memo: formData.입금적요,
                telegram_id: formData.텔레그램아이디,
                telegram_noti_yn: formData.텔레그램알림사용여부,
                withdrawal_fee: formData.출금수수료,
                wallet_use_yn: formData.지갑사용여부
            }, {
                withCredentials: true,
            }).then((response) => {
                let tmp = response?.data;
                if (tmp?.code < 2000) {
                    setMessage({ msg: "에이전트 등록에 성공했습니다." });
                    setReload(e => !e);
                    setFormData(initialFormData);
                } else {
                    setMessage({ msg: tmp?.msg ?? "에이전트 등록에 실패했습니다." });
                }
            }).catch(error => {
                setMessage({ msg: "에이전트 등록에 실패했습니다." });
                console.error(error);
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setFormData(initialFormData);
    };

    return (
        <div style={{ padding: "5px" }}>
            <div style={{
                padding: '0 0 0.5rem',
                fontSize: "12px",
                display: 'flex',
                flexDirection: "column",
            }}>
                <div className="list-info-box">
                    <div className="gap" style={{ borderTop: "1px solid #e1e1e1" }}>
                        <ul>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>PG코드</span>
                                    <input
                                        id="PG코드"
                                        name="PG코드"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="30"
                                        value={formData.PG코드}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>에이전트등급</span>
                                    <select
                                        id="에이전트등급"
                                        name="에이전트등급"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.에이전트등급}
                                        onChange={handleChange}
                                    >
                                        <option value="">선택하세요</option>
                                        <option value={1}>지사</option>
                                        <option value={2}>총판</option>
                                        <option value={3}>대리점</option>
                                        <option value={4}>영업자1</option>
                                        <option value={5}>영업자2</option>
                                        <option value={6}>영업자3</option>
                                    </select>
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>상위에이전트</span>
                                    <select
                                        id="상위에이전트"
                                        name="상위에이전트"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.상위에이전트}
                                        onChange={handleChange}
                                        disabled={formData.에이전트등급 == '1' ? true : false}
                                    >
                                        <option value="">상위에이전트를 선택해주세요.</option>
                                        {levelData.map(function (a, i) {
                                            return (
                                                <option key={i} value={a.code}>{a.nm}</option>
                                            )
                                        })}
                                    </select>
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>에이전트ID</span>
                                    <input
                                        id="에이전트ID"
                                        name="에이전트ID"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="30"
                                        value={formData.에이전트ID}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>에이전트명</span>
                                    <input
                                        id="에이전트명"
                                        name="에이전트명"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="14"
                                        value={formData.에이전트명}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>에이전트수수료</span>
                                    <input
                                        id="에이전트수수료"
                                        name="에이전트수수료"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="10"
                                        value={formData.에이전트수수료}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>정산은행</span>
                                    <select
                                        id="정산은행"
                                        name="정산은행"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.정산은행}
                                        onChange={handleChange}
                                    >
                                        {bank.map((a, i) => (
                                            <option key={i} value={a.name}>
                                                {a.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>계좌번호</span>
                                    <input
                                        id="계좌번호"
                                        name="계좌번호"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px - 76px)", border: "none", outline: "none" }}
                                        value={formData.계좌번호}
                                        onChange={handleChange}
                                    />
                                    <button
                                        type="button"
                                        onClick={verifyHolder}
                                        disabled={verifyingHolder}
                                        style={verifyBtnStyle(verifyingHolder)}
                                    >
                                        {verifyingHolder ? "조회중" : "계좌 확인"}
                                    </button>
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>예금주</span>
                                    <input
                                        id="예금주"
                                        name="예금주"
                                        type="text"
                                        className="right-text"
                                        style={{ width: holderVerified ? "calc(100% - 122px - 60px)" : "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.예금주}
                                        onChange={handleChange}
                                    />
                                    {holderVerified && (
                                        <span style={verifiedBadgeStyle}>확인됨</span>
                                    )}
                                </div>
                            </li>
                            {/* <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>세금계산서 Email</span>
                                    <input
                                        id="세금계산서Email"
                                        name="세금계산서Email"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="13"
                                        value={formData.세금계산서Email}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li> */}
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>입금적요</span>
                                    <input
                                        id="입금적요"
                                        name="입금적요"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="50"
                                        value={formData.입금적요}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>텔레그램ID</span>
                                    <input
                                        id="텔레그램아이디"
                                        name="텔레그램아이디"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="50"
                                        value={formData.텔레그램아이디}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>텔레그램 알림사용</span>
                                    <select
                                        id="텔레그램알림사용여부"
                                        name="텔레그램알림사용여부"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.텔레그램알림사용여부}
                                        onChange={handleChange}
                                    >
                                        <option value="Y">사용</option>
                                        <option value="N">미사용</option>
                                    </select>
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>출금수수료(정액제)</span>
                                    <input
                                        id="출금수수료"
                                        name="출금수수료"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="10"
                                        value={formData.출금수수료}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>지갑 사용 여부</span>
                                    <select
                                        id="지갑사용여부"
                                        name="지갑사용여부"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.지갑사용여부}
                                        onChange={handleChange}
                                    >
                                        <option value="Y">사용</option>
                                        <option value="N">미사용</option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="modal-footer" style={{ marginTop: "16px", display: "flex", gap: "8px" }}>
                    <button
                        type="button"
                        className="btn btn-primary w-20 reg_btn"
                        onClick={onSubmit}
                        disabled={isSubmitting}
                    >
                        등록
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary w-20 close_btn"
                        onClick={handleCancel}
                        style={{
                            backgroundColor: "darkred",
                            border: "none"
                        }}
                    >
                        취소
                    </button>
                </div>
            </div>
        </div>
    );
}

function UpdateInfo({ setMessage, clickData, setReload }) {
    const MyData = useSelector((state) => state.MyData);
    const brand = useSelector((state) => state.brand);
    const [levelData, setLevelData] = useState([])
    const [formData, setFormData] = useState({});

    useEffect(() => {
        const newFormData = {
            에이전트등급: clickData.auth_cd || '',
            상위에이전트: clickData.parent_code || '',
            에이전트ID: clickData.agent_id || '',
            에이전트명: clickData.nm || '',
            에이전트수수료: Number(clickData.agent_fee) || 0,
            정산은행: clickData.bank_nm || '',
            정산은행코드: clickData.bank_cd || bank.find(b => b.name === clickData.bank_nm)?.code || '',
            계좌번호: clickData.accnt_no || '',
            예금주: clickData.accnt_nm || '',
            세금계산서Email: clickData.tax_email || '',
            PG코드: clickData.co_id || '',
            입금적요: clickData.payout_memo || '',
            등록일: clickData.reg_dt || '',
            수정일: clickData.mod_dt || '',
            텔레그램아이디: clickData.telegram_id || '',
            텔레그램알림사용여부: clickData.telegram_noti_yn || 'N',
            출금수수료: clickData.withdrawal_fee || '',
            지갑사용여부: clickData.wallet_use_yn || 'N'
        };
        setFormData(newFormData);
        LevelList()
    }, [clickData]);

    const LevelList = async () => {
        await axios.get(process.env.REACT_APP_API + `/api/agent/up-list`, {
            withCredentials: true,
            params: {
                level: clickData.auth_cd
            }
        }).then((response) => {
            let tmp = response?.data
            if (tmp?.code < 2000) {
                setLevelData(tmp?.data?.agent ?? [])
            }
            else {
                setMessage({ msg: "상위리스트 업로드에 실패했습니다." })
            }
        }).catch(error => {
            console.error(error);
        });
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === '정산은행') {
            const selected = bank.find(b => b.name === value);
            setFormData({
                ...formData,
                정산은행: value,
                정산은행코드: selected?.code || ''
            });
        } else {
            setFormData({
                ...formData,
                [name]: value
            });
        }
    };

    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);

        if (window.confirm("수정하시겠습니까?")) {
            try {
                await axios.post(process.env.REACT_APP_API + "/api/agent/update", {
                    id: clickData.id,
                    auth_cd: formData.에이전트등급,
                    agent_id: formData.에이전트ID,
                    nm: formData.에이전트명,
                    agent_fee: formData.에이전트수수료,
                    parent_code: formData.상위에이전트,
                    bank_nm: formData.정산은행,
                    bank_cd: formData.정산은행코드,
                    accnt_no: formData.계좌번호,
                    accnt_nm: formData.예금주,
                    // tax_email: formData.세금계산서Email,
                    co_id: formData.PG코드,
                    payout_memo: formData.입금적요,
                    telegram_id: formData.텔레그램아이디,
                    telegram_noti_yn: formData.텔레그램알림사용여부,
                    withdrawal_fee: formData.출금수수료,
                    wallet_use_yn: formData.지갑사용여부
                }, {
                    withCredentials: true,
                }).then((response) => {
                    let tmp = response?.data;
                    if (tmp?.code < 2000) {
                        setMessage({ msg: "에이전트 수정에 성공했습니다." });
                        setReload(e => !e);
                    } else {
                        setMessage({ msg: "에이전트 수정에 실패했습니다." });
                    }
                }).catch(error => {
                    setMessage({ msg: "에이전트 수정에 실패했습니다." });
                    console.error(error);
                });
            } finally {
                setIsSubmitting(false);
            }
        } else {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        const resetFormData = {
            에이전트등급: clickData.auth_cd || '',
            상위에이전트: clickData.parent_code || '',
            에이전트ID: clickData.agent_id || '',
            에이전트명: clickData.nm || '',
            에이전트수수료: Number(clickData.agent_fee) || 0,
            정산은행: clickData.bank_nm || '',
            정산은행코드: clickData.bank_cd || bank.find(b => b.name === clickData.bank_nm)?.code || '',
            계좌번호: clickData.accnt_no || '',
            예금주: clickData.accnt_nm || '',
            세금계산서Email: clickData.tax_email || '',
            PG코드: clickData.co_id || '',
            입금적요: clickData.payout_memo || '',
            등록일: clickData.reg_dt || '',
            수정일: clickData.mod_dt || '',
            텔레그램아이디: clickData.telegram_id || '',
            텔레그램알림사용여부: clickData.telegram_noti_yn || 'N',
            출금수수료: clickData.withdrawal_fee || '',
            지갑사용여부: clickData.wallet_use_yn || 'N'
        };
        setFormData(resetFormData);
    };

    const isDisabled = MyData?.role !== "SUPER";

    // 예금주 조회
    const { verifying: verifyingHolder, isVerified: holderVerified, verify: verifyHolder } =
        useAccountHolderVerify({ formData, setFormData, setMessage });

    return (
        <div style={{ padding: "5px" }}>
            <div style={{
                padding: '0 0 0.5rem',
                fontSize: "12px",
                display: 'flex',
                flexDirection: "column",
            }}>
                <div className="list-info-box">
                    <div className="gap" style={{ borderTop: "1px solid #e1e1e1" }}>
                        <ul>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>PG코드</span>
                                    <input
                                        id="PG코드"
                                        name="PG코드"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="30"
                                        value={formData.PG코드}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            {!MyData?.hideElements && (
                                <li>
                                    <div className="inner" style={{ width: "100%" }}>
                                        <span className="left-text" style={{ width: "120px", height: "34px" }}>에이전트등급</span>
                                        <select
                                            id="에이전트등급"
                                            name="에이전트등급"
                                            className="right-text"
                                            style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                            value={formData.에이전트등급}
                                            onChange={handleChange}
                                            disabled
                                        >
                                            <option value="">선택하세요</option>
                                            <option value={1}>지사</option>
                                            <option value={2}>총판</option>
                                            <option value={3}>대리점</option>
                                            <option value={4}>영업자1</option>
                                            <option value={5}>영업자2</option>
                                            <option value={6}>영업자3</option>
                                        </select>
                                    </div>
                                </li>
                            )}
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>상위에이전트</span>
                                    <select
                                        id="상위에이전트"
                                        name="상위에이전트"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.상위에이전트}
                                        onChange={handleChange}
                                        disabled={MyData?.role != "SUPER" ? true : formData.에이전트등급 == '1' ? true : false}
                                    >
                                        <option value="">상위에이전트를 선택해주세요.</option>
                                        {levelData.map(function (a, i) {
                                            const displayName = (brand.topAgentId && clickData.agent_id === brand.topAgentId) ? brand.name : a.nm;
                                            return (
                                                <option key={i} value={a.code}>{displayName}</option>
                                            )
                                        })}
                                    </select>
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>에이전트ID</span>
                                    <input
                                        id="에이전트ID"
                                        name="에이전트ID"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="30"
                                        value={formData.에이전트ID}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>에이전트명</span>
                                    <input
                                        id="에이전트명"
                                        name="에이전트명"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="14"
                                        value={formData.에이전트명}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>에이전트수수료</span>
                                    <input
                                        id="에이전트수수료"
                                        name="에이전트수수료"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="10"
                                        value={formData.에이전트수수료}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>정산은행</span>
                                    <select
                                        id="정산은행"
                                        name="정산은행"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.정산은행}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    >
                                        {bank.map((a, i) => (
                                            <option key={i} value={a.name}>
                                                {a.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>계좌번호</span>
                                    <input
                                        id="계좌번호"
                                        name="계좌번호"
                                        type="text"
                                        className="right-text"
                                        style={{ width: isDisabled ? "calc(100% - 122px)" : "calc(100% - 122px - 76px)", border: "none", outline: "none" }}
                                        value={formData.계좌번호}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    />
                                    {!isDisabled && (
                                        <button
                                            type="button"
                                            onClick={verifyHolder}
                                            disabled={verifyingHolder}
                                            style={verifyBtnStyle(verifyingHolder)}
                                        >
                                            {verifyingHolder ? "조회중" : "계좌 확인"}
                                        </button>
                                    )}
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>예금주</span>
                                    <input
                                        id="예금주"
                                        name="예금주"
                                        type="text"
                                        className="right-text"
                                        style={{ width: holderVerified ? "calc(100% - 122px - 60px)" : "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.예금주}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    />
                                    {holderVerified && (
                                        <span style={verifiedBadgeStyle}>확인됨</span>
                                    )}
                                </div>
                            </li>
                            {/* <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>세금계산서 Email</span>
                                    <input
                                        id="세금계산서Email"
                                        name="세금계산서Email"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="13"
                                        value={formData.세금계산서Email}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li> */}
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>입금적요</span>
                                    <input
                                        id="입금적요"
                                        name="입금적요"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="50"
                                        value={formData.입금적요}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>등록/수정일</span>
                                    <input
                                        id="등록수정일"
                                        name="등록수정일"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={
                                            (formData.등록일 ? formData.등록일.slice(0, 16) : '') +
                                            (formData.등록일 && formData.수정일 ? ' / ' : '') +
                                            (formData.수정일 ? formData.수정일.slice(0, 16) : '')
                                        }
                                        disabled={true}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>텔레그램ID</span>
                                    <input
                                        id="텔레그램아이디"
                                        name="텔레그램아이디"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="50"
                                        value={formData.텔레그램아이디}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>텔레그램 알림사용</span>
                                    <select
                                        id="텔레그램알림사용여부"
                                        name="텔레그램알림사용여부"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.텔레그램알림사용여부}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    >
                                        <option value="Y">사용</option>
                                        <option value="N">미사용</option>
                                    </select>
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>출금수수료(정액제)</span>
                                    <input
                                        id="출금수수료"
                                        name="출금수수료"
                                        type="text"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        maxLength="10"
                                        value={formData.출금수수료}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    />
                                </div>
                            </li>
                            <li>
                                <div className="inner" style={{ width: "100%" }}>
                                    <span className="left-text" style={{ width: "120px", height: "34px" }}>지갑 사용 여부</span>
                                    <select
                                        id="지갑사용여부"
                                        name="지갑사용여부"
                                        className="right-text"
                                        style={{ width: "calc(100% - 122px)", border: "none", outline: "none" }}
                                        value={formData.지갑사용여부}
                                        disabled={isDisabled}
                                        onChange={handleChange}
                                    >
                                        <option value="Y">사용</option>
                                        <option value="N">미사용</option>
                                    </select>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="modal-footer" style={{ marginTop: "16px", display: "flex", gap: "8px" }}>
                    {MyData?.role == "SUPER" && (
                        <>
                            <button
                                type="button"
                                className="btn btn-primary w-20 reg_btn"
                                onClick={onSubmit}
                                disabled={isSubmitting}
                            >
                                수정
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary w-20 close_btn"
                                onClick={handleCancel}
                                style={{ backgroundColor: "darkred", border: "none" }}
                            >
                                취소
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function SubmerchantsList({ setMessage, clickData, setReload }) {
    const navigate = useNavigate();
    const MyData = useSelector((state) => state.MyData)

    const [page, setPage] = useState(1)
    const [maxFirstPage, setMaxFirstPage] = useState(20)
    const [listData, setListData] = useState([])
    const [combineData, setCombineData] = useState([])
    const [combineList, setCombineList] = useState([])
    const [listState, setListState] = useState(false)

    useEffect(() => {
        fetchAgentSubData();
    }, [clickData]);

    const fetchAgentSubData = async () => {
        try {
            const res = await axios.get(process.env.REACT_APP_API + "/api/agent/merchant-list", {
                params: {
                    agentId: clickData.agent_id
                },
                withCredentials: true,
            });

            if (res.data?.code < 2000) {
                setListData(res?.data?.data?.merchant ?? [])
            } else if (res.data?.code === 9999) {
                navigate("/");
            }
        } catch (err) {
            console.error(err);
            navigate("/");
        }
    };

    const tableHeaderData = [
        { "no": 1, "text": "가맹점명", "width": "150px", "textAlign": "center" },
        { "no": 2, "text": "수수료", "width": "80px", "textAlign": "center" },
        { "no": 3, "text": "매출", "width": "100px", "textAlign": "center" },
    ]

    useEffect(() => {
        let tableBodyData = [];
        if (listData) {
            setCombineList(listData)
            let list = listData
            for (let i = 0; i < list.length; i++) {
                let data = list[i]

                let rowData = [
                    { "no": 1, "text": data.co_nm, "width": "150px", "textAlign": "left" },
                    { "no": 2, "text": `${Number(data.fee)}%`, "width": "80px", "textAlign": "right" },
                    { "no": 3, "text": changeNum(Number(data.total_amount)), "width": "100px", "textAlign": "right" },
                ]

                rowData.id = data.id
                tableBodyData.push(rowData);
            }
            setListState(e => !e)
            setCombineData(tableBodyData)
        }
    }, [listData, page, maxFirstPage])

    return (
        <div style={{ padding: "5px" }}>
            <div style={{
                padding: '0 0 0.5rem',
                fontSize: "12px",
            }}>
                <List
                    tableHeaderData={tableHeaderData}
                    tableBodyData={combineList ?? []}
                    filter_tableBodyData={combineData}
                    page={page}
                    setPage={setPage}
                    maxFirstPage={maxFirstPage}
                    listType={true}
                    listState={listState}
                    nonePage={false}
                    scrollState={true}
                    height={"550px"}
                />
            </div>
        </div>
    );
}

export default IntegrateAgentV2;
